import {makeAutoObservable, observable, reaction, runInAction} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {HeadToHeadMode, RequestState, ModalType} from "data/enums";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {extractErrorMessage, trackSentryErrors} from "data/utils";
import type {IRosterStore} from "data/stores/roster/roster.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {AdobeDTM} from "data/utils/AdobeDTM";

export interface IHeadToHeadController extends ViewController {
	i18n: ILocalizationStore;
	get activeMode(): HeadToHeadMode;
	get isLoading(): boolean;
	setActiveMode: (type: HeadToHeadMode) => void;
}

@injectable()
export class HeadToHeadController implements IHeadToHeadController {
	@observable private _headToHeadMode: HeadToHeadMode = HeadToHeadMode.Manage;
	@observable private _requestState: RequestState = RequestState.IDLE;
	private _disposers: (() => void)[] = [];

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) public readonly _contestStore: IContestsStore,
		@inject(Bindings.RosterStore) public readonly _rosterStore: IRosterStore,
		@inject(Bindings.ModalsStore) public readonly _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get contest() {
		return this._contestStore.currentContest;
	}

	get isLoading() {
		return [RequestState.IDLE, RequestState.PENDING].includes(this._requestState);
	}
	get contestId(): number {
		return this.contest?.id ?? 0;
	}

	get activeMode(): HeadToHeadMode {
		return this._headToHeadMode;
	}

	setActiveMode = (type: HeadToHeadMode): void => {
		this._headToHeadMode = type;
	};

	init() {
		this._disposers = [
			reaction(
				() => this.contestId,
				() => this.fetchData(),
				{fireImmediately: true}
			),
			reaction(
				() => this.activeMode,
				(activeMode) => {
					const stateString =
						activeMode === HeadToHeadMode.Manage ? "Manage Games" : "Friends";
					AdobeDTM.trackState({
						"page.legacyName": `Major League Baseball: Spinball: H2H: ${stateString}`,
					});
				},
				{fireImmediately: true}
			),
		];
	}

	dispose() {
		this._disposers.forEach((dispose) => dispose());
	}

	private fetchData = () => {
		const contest = this.contest;
		this._requestState = RequestState.PENDING;
		if (contest) {
			this._rosterStore.updateContestId(contest.id);
			void Promise.all([
				this._rosterStore.fetchRoster(),
				this._rosterStore.fetchPlayedContests(),
			])
				.then(() => {
					runInAction(() => {
						this._requestState = RequestState.SUCCESS;
					});
				})
				.catch((e) => {
					runInAction(() => {
						this._requestState = RequestState.ERROR;
					});
					this._modalsStore.showModal(ModalType.ERROR, {
						message: extractErrorMessage(e),
					});
					trackSentryErrors(e, {}, "h2h.fetchData");
				});
		}
	};
}
